import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { cn } from '../../lib/cn.js'

const controlClasses =
  'h-10 w-full rounded-md border border-border bg-surface-raised px-3 text-sm ' +
  'placeholder:text-text-muted disabled:bg-surface-sunken disabled:text-text-muted'

interface FieldShellProps {
  label: string
  hint?: string
  error?: string | undefined
  children: (props: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode
}

/**
 * Label, control, hint and error as one unit.
 *
 * The wiring — `htmlFor`, `aria-describedby`, `aria-invalid`, `role="alert"` —
 * lives here so no individual form has to remember it. A validation message
 * that a screen reader never announces is not a validation message.
 */
function FieldShell({ label, hint, error, children }: FieldShellProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {hint && !error && (
        <p id={hintId} className="text-text-muted text-xs">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-danger text-xs">
          {error}
        </p>
      )}
    </div>
  )
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: string
  error?: string | undefined
}

export function TextField({ label, hint, error, className, ...props }: TextFieldProps) {
  return (
    <FieldShell label={label} {...(hint ? { hint } : {})} error={error}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={cn(controlClasses, invalid && 'border-danger', className)}
          {...props}
        />
      )}
    </FieldShell>
  )
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: string
  error?: string | undefined
  options: { value: string; label: string }[]
}

export function SelectField({
  label,
  hint,
  error,
  options,
  className,
  ...props
}: SelectFieldProps) {
  return (
    <FieldShell label={label} {...(hint ? { hint } : {})} error={error}>
      {({ id, describedBy, invalid }) => (
        <select
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={cn(controlClasses, invalid && 'border-danger', className)}
          {...props}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </FieldShell>
  )
}

interface CheckboxFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string
  hint?: string
}

export function CheckboxField({ label, hint, className, ...props }: CheckboxFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <div className="flex items-start gap-2">
      <input
        id={id}
        type="checkbox"
        aria-describedby={hint ? hintId : undefined}
        // A disabled checkbox that looks enabled invites a click that does
        // nothing, and the reader concludes the page is broken rather than that
        // the setting is fixed. The label dims with it, because the box alone is
        // too small a signal.
        className={cn(
          'accent-accent mt-0.5 size-4',
          props.disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        {...props}
      />
      <div className="flex flex-col gap-0.5">
        <label htmlFor={id} className={cn('text-sm', props.disabled && 'text-text-muted')}>
          {label}
        </label>
        {hint && (
          <p id={hintId} className="text-text-muted text-xs">
            {hint}
          </p>
        )}
      </div>
    </div>
  )
}
